import { formatDate } from '@angular/common';

export function createUrl(url: string): string {
    const host = window.location.protocol + '//' + window.location.hostname;
    return url.replace('{host}', host);
}

export function getOrganization(defaultTenant: string): string {
    const hostname = window.location.hostname;

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return defaultTenant;
    }

    let domainParts = hostname.split('.');
    return domainParts[0];
}

export function isValidDate(date: Date): boolean {
    return date instanceof Date && !isNaN(date.getTime());
}

const DATE_TIME_FORMAT_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;

export function isDateTimeFormatValid(dateTimeString: string): boolean {
    return DATE_TIME_FORMAT_REGEX.test(dateTimeString);
}

const DATE_FORMAT = 'yyyy-MM-dd';
const TIME_FORMAT = 'HH:mm:00';
const DATE_TIME_FORMAT_LOCALE = 'en-US';

export function createFormattedDateAndTime(date: Date | string, time?: Date | string): string {
    const formattedDate = formatDate(date, DATE_FORMAT, DATE_TIME_FORMAT_LOCALE);
    const formattedTime = formatDate(time ? time : date, TIME_FORMAT, DATE_TIME_FORMAT_LOCALE);
    const formattedDateTime = formattedDate + 'T' + formattedTime;
    return formattedDateTime;
}

/**
 * Formats the given instant as a naive yyyy-MM-ddTHH:mm:00 string carrying the
 * wall-clock time in the given IANA timezone (the backend interprets naive
 * pickup times at the pickup location, i.e. tenant time — not browser time).
 * Falls back to the browser-local format when the timezone is missing/invalid.
 */
export function createFormattedDateAndTimeInTimeZone(date: Date, ianaTimeZoneId: string | null | undefined): string {
    if (ianaTimeZoneId) {
        try {
            const parts = new Intl.DateTimeFormat('en-US', {
                timeZone: ianaTimeZoneId,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hourCycle: 'h23',
            }).formatToParts(date);
            const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
            const formattedDateTime = `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}:00`;
            if (isDateTimeFormatValid(formattedDateTime)) {
                return formattedDateTime;
            }
        } catch {
            // invalid IANA id — fall through to browser-local formatting
        }
    }
    return createFormattedDateAndTime(date);
}

export function isNotNullOrUndefined<T>(input: null | undefined | T): input is T {
    return input !== null && input !== undefined;
}
